import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { toast } from "sonner";
import { 
  Building2, 
  Search, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  MapPin, 
  Sparkles, 
  Truck, 
  Eye, 
  Heart, 
  X, 
  Send, 
  DollarSign,
  Compass,
  ArrowRight,
  ShieldCheck,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  FileText,
  Users
} from "lucide-react";

// Warm progress state shown when tenant has briefs but no concierge matches yet
const ConciergeReviewingState = ({ briefs }) => {
  const brief = briefs[0] || {};
  const submittedAt = brief.created_at ? new Date(brief.created_at) : new Date();
  const hoursSince = Math.max(0, Math.floor((Date.now() - submittedAt.getTime()) / 3600000));
  const estWindow = hoursSince < 4 ? "Expected within the next few hours" : hoursSince < 24 ? "Expected later today" : "Expected within one business day";

  const steps = [
    { label: "Brief received", detail: "Your criteria are logged with the concierge desk.", state: "done", icon: FileText },
    { label: "Concierge reviewing", detail: "A London operator is pairing your brief against private inventory and vetted feeds.", state: "active", icon: Users },
    { label: "Matches curated", detail: "Hand-picked properties will appear here for you to review.", state: "upcoming", icon: Sparkles },
    { label: "Private viewings", detail: "Request viewings on your terms once you approve a match.", state: "upcoming", icon: Eye },
  ];

  return (
    <div data-testid="tenant-concierge-reviewing" className="rounded-2xl bg-white border border-[#E5EBF5] overflow-hidden">
      <div className="p-6 sm:p-8 bg-gradient-to-b from-[#F9FBFF] to-white border-b border-[#E5EBF5]">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center space-x-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#334DAF] opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#091F5C]" />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Concierge Desk · Live</div>
              <div className="text-lg font-heading font-semibold text-[#091F5C] mt-0.5">Your brief is being reviewed.</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#4A5B78] font-semibold">Status</div>
            <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">{estWindow}</div>
          </div>
        </div>
        <p className="mt-4 text-sm text-[#4A5B78] font-light leading-relaxed max-w-2xl">
          Thank you — we've received your search brief. A London concierge operator is now pairing it against private landlord submissions, vetted agency stock and off-market residences. You'll see curated matches here the moment they're ready. No spam, no cold calls, no pressure.
        </p>
      </div>

      <ol className="divide-y divide-[#E5EBF5]">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const isDone = s.state === "done";
          const isActive = s.state === "active";
          return (
            <li key={i} className="flex items-start gap-4 px-6 sm:px-8 py-5">
              <div className={`flex-shrink-0 w-9 h-9 flex items-center justify-center border ${
                isDone ? "bg-[#091F5C] border-[#091F5C] text-white" :
                isActive ? "bg-[#E8F2FE] border-[#334DAF] text-[#091F5C]" :
                "bg-white border-[#E5EBF5] text-[#7096D1]"
              }`}>
                {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-sm font-heading font-semibold ${isActive || isDone ? "text-[#091F5C]" : "text-[#4A5B78]"}`}>
                    {s.label}
                  </span>
                  {isActive && (
                    <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[#334DAF] text-[#334DAF] font-semibold">
                      In progress
                    </span>
                  )}
                  {isDone && (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#334DAF] font-semibold">
                      Complete · {hoursSince}h ago
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#4A5B78] font-light mt-1 leading-relaxed">{s.detail}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="px-6 sm:px-8 py-4 bg-[#F9FBFF] border-t border-[#E5EBF5] flex items-center justify-between gap-4 flex-wrap">
        <span className="text-xs text-[#4A5B78] font-light">
          You'll receive an update as soon as your first match is dispatched.
        </span>
        <Link
          to="/find"
          data-testid="tenant-refine-brief-btn"
          className="text-xs font-semibold text-[#091F5C] hover:text-[#334DAF] inline-flex items-center gap-1"
        >
          Refine brief <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

export const TenantDashboard = () => {
  const { user, token } = useAuth();

  const [briefs, setBriefs] = useState([]);
  const [matches, setMatches] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [moveRequests, setMoveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Modal State for Viewing Request
  const [selectedMatchForViewing, setSelectedMatchForViewing] = useState(null);
  const [viewingForm, setViewingForm] = useState({
    preferred_date: "2026-07-02",
    preferred_time_slot: "Morning (09:00 - 12:00)",
    comments: "Looking forward to seeing the terrace and parking arrangements."
  });
  const [viewingSubmitting, setViewingSubmitting] = useState(false);

  // Match Ready Banner state — tracks new matches since the user last viewed the dashboard
  const seenKey = user?.id ? `findlet_seen_matches_${user.id}` : "findlet_seen_matches_anon";
  const [newMatchCount, setNewMatchCount] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const [briefsRes, matchesRes, viewingsRes, movesRes] = await Promise.all([
        api.get("/briefs"),
        api.get("/matches"),
        api.get("/viewings"),
        api.get("/move-requests")
      ]);
      setBriefs(briefsRes.data);
      setMatches(matchesRes.data);
      setViewings(viewingsRes.data);
      setMoveRequests(movesRes.data);

      // Compare fetched match count against last-seen count for this tenant
      const previouslySeen = parseInt(localStorage.getItem(seenKey) || "0", 10);
      const currentCount = matchesRes.data.length;
      if (currentCount > previouslySeen) {
        setNewMatchCount(currentCount - previouslySeen);
        setBannerDismissed(false);
      } else {
        setNewMatchCount(0);
      }
    } catch (err) {
      console.error("Dashboard fetch error", err);
      setFetchError("Unable to load live portal data. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeNewMatches = () => {
    localStorage.setItem(seenKey, String(matches.length));
    setBannerDismissed(true);
  };

  useEffect(() => {
    fetchData();
  }, [user, token]);

  const handleRequestViewingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMatchForViewing) return;
    setViewingSubmitting(true);
    try {
      await api.post("/viewings", {
        match_id: selectedMatchForViewing.id,
        property_id: selectedMatchForViewing.property_id,
        preferred_date: viewingForm.preferred_date,
        preferred_time_slot: viewingForm.preferred_time_slot,
        comments: viewingForm.comments
      });
      toast.success("Viewing request submitted! Host and Concierge notified.");
      setSelectedMatchForViewing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request viewing.");
    } finally {
      setViewingSubmitting(false);
    }
  };

  const handleShortlistMatch = async (matchId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Shortlisted" ? "Available" : "Shortlisted";
      await api.patch(`/matches/${matchId}`, { status: newStatus });
      toast.success(newStatus === "Shortlisted" ? "Saved to your shortlist" : "Removed from shortlist");
      fetchData();
    } catch (e) {
      toast.error("Could not update match status");
    }
  };

  const handleDeclineMatch = async (matchId) => {
    try {
      await api.patch(`/matches/${matchId}`, { status: "Declined" });
      toast.info("Match marked as not suitable. Concierge desk notified.");
      fetchData();
    } catch (e) {
      toast.error("Could not decline match");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Welcome Header */}
        <div className="bg-[#091F5C] text-white rounded-2xl p-6 sm:p-8 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-900/80 border border-[#334DAF] text-[#D0E4FE] text-xs font-semibold">
              <Compass className="w-3.5 h-3.5" />
              <span>Tenant Portal • Demand Desk</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold font-heading">
              Welcome, {user?.name}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
              Track your active London search brief, review curated concierge property matches, coordinate private viewings, and book FIND LET Move relocation.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={fetchData}
              data-testid="tenant-dash-refresh-btn"
              className="px-3.5 py-2 rounded-xl bg-[#091F5C] hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center space-x-1.5 border border-slate-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>Refresh</span>
            </button>
            <Link
              to="/find"
              data-testid="tenant-dash-new-brief-btn"
              className="px-4 py-2.5 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-xs flex items-center space-x-1.5 shadow"
            >
              <Search className="w-4 h-4" />
              <span>Update / New Brief</span>
            </Link>
            <Link
              to="/move"
              data-testid="tenant-dash-book-move-btn"
              className="px-4 py-2.5 rounded-xl bg-[#091F5C] hover:bg-slate-700 text-white font-semibold text-xs flex items-center space-x-1.5 border border-slate-700"
            >
              <Truck className="w-4 h-4 text-[#7096D1]" />
              <span>Book FIND LET Move</span>
            </Link>
          </div>
        </div>

        {/* Match Ready Banner — soft, dismissible, shown when new matches arrived since last visit */}
        {newMatchCount > 0 && !bannerDismissed && (
          <div
            data-testid="tenant-match-ready-banner"
            className="rounded-2xl border border-[#D0E4FE] bg-[#E8F2FE] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="flex items-start sm:items-center gap-4 flex-1">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-[#D0E4FE] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#334DAF]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">
                  {newMatchCount === 1 ? "New match ready" : `${newMatchCount} new matches ready`}
                </div>
                <div className="text-sm sm:text-base font-heading font-semibold text-[#091F5C] mt-0.5">
                  Your concierge just curated {newMatchCount === 1 ? "a property" : `${newMatchCount} properties`} for you.
                </div>
                <p className="text-xs text-[#4A5B78] mt-1 font-light">
                  Review below and request a private viewing on any home that suits you.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <a
                href="#curated-matches"
                data-testid="tenant-match-banner-review"
                onClick={acknowledgeNewMatches}
                className="px-4 py-2 bg-[#091F5C] hover:bg-[#061745] text-white text-xs font-semibold tracking-wide inline-flex items-center gap-1.5"
              >
                Review matches <ChevronRight className="w-3 h-3" />
              </a>
              <button
                onClick={acknowledgeNewMatches}
                data-testid="tenant-match-banner-dismiss"
                aria-label="Dismiss"
                className="p-2 text-[#4A5B78] hover:text-[#091F5C] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Error / Retry Banner if API fetch failed */}
        {fetchError && (
          <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
              <span>{fetchError}</span>
            </div>
            <button
              onClick={fetchData}
              className="px-3 py-1 rounded-lg bg-rose-600 text-white font-semibold text-xs"
            >
              Retry
            </button>
          </div>
        )}

        {/* SECTION 1: Active Search Brief Status Card */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
              <Search className="w-5 h-5 text-[#334DAF]" />
              <span>Active Search Brief Status</span>
            </h2>
          </div>

          {loading ? (
            <div className="p-8 rounded-2xl bg-white border border-[#E5EBF5] text-center text-xs text-[#4A5B78]">
              <div className="w-6 h-6 border-2 border-[#D0E4FE] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              Loading your London search brief...
            </div>
          ) : briefs.length === 0 ? (
            <div className="p-8 rounded-2xl bg-white border border-[#E5EBF5] text-center space-y-4">
              <Compass className="w-10 h-10 text-slate-400 mx-auto" />
              <div className="space-y-1">
                <h3 className="text-base font-bold text-[#091F5C]">No active search briefs yet</h3>
                <p className="text-xs text-[#4A5B78]">Define your London home criteria to let our concierge match you.</p>
              </div>
              <Link
                to="/find"
                className="inline-block px-5 py-2.5 rounded-xl bg-[#091F5C] text-white font-semibold text-xs"
              >
                Create Search Brief →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {briefs.map((b) => (
                <div key={b.id} className="bg-white rounded-2xl p-6 border border-[#E5EBF5] shadow-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono font-bold text-[#4A5B78]">
                      REF: {b.id.slice(0, 8).toUpperCase()}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      b.status === "Matches Available"
                        ? "bg-[#E8F2FE] text-[#091F5C] border-[#D0E4FE]"
                        : "bg-[#F9FBFF] text-[#334DAF] border-[#D0E4FE]"
                    }`}>
                      {b.status === "Active / Matching" ? "Concierge Reviewing" : b.status}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs text-[#4A5B78]">
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#091F5C]">Target Boroughs:</span>
                      <span className="text-[#334DAF] font-medium">{b.target_boroughs?.join(", ")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#091F5C]">Budget Range:</span>
                      <span className="font-bold text-[#091F5C]">£{b.budget_pcm_min?.toLocaleString()} - £{b.budget_pcm_max?.toLocaleString()} PCM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#091F5C]">Move-In Window:</span>
                      <span>{b.move_in_date} ({b.tenancy_length_months || 12} Months)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#091F5C]">Requirements:</span>
                      <span>
                        {b.pets ? "Pets Approved • " : ""}
                        {b.outside_space ? "Balcony/Garden • " : ""}
                        {b.furnishing_preference}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 border-t text-[11px] text-[#4A5B78] flex items-center justify-between">
                    <span>Assigned Concierge Desk: London Central</span>
                    <span className="text-[#334DAF] font-medium">Search Active</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 2: Concierge Curated Matches */}
        <div className="space-y-4 pt-4" id="curated-matches">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-[#7096D1]" fill="currentColor" />
              <span>Concierge Curated Property Matches ({matches.length})</span>
            </h2>
            <span className="text-xs text-[#4A5B78]">Hand-paired by your concierge desk</span>
          </div>

          {loading ? (
            <div className="p-8 rounded-2xl bg-white border border-[#E5EBF5] text-center text-xs text-[#4A5B78]">
              Loading curated matches...
            </div>
          ) : matches.length === 0 ? (
            <ConciergeReviewingState briefs={briefs} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {matches.map((m) => {
                const prop = m.property || {};
                const isShortlisted = m.status === "Shortlisted";
                const isDeclined = m.status === "Declined";

                if (isDeclined) return null;

                return (
                  <div 
                    key={m.id}
                    data-testid={`matched-property-card-${m.id}`}
                    className="bg-white rounded-2xl border border-[#E5EBF5] overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      {/* Image Preview */}
                      <div className="h-48 overflow-hidden relative bg-[#E8F2FE]">
                        {prop.images && prop.images.length > 0 ? (
                          <img
                            src={prop.images[0]}
                            alt={prop.title || m.property_title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <Building2 className="w-8 h-8" />
                          </div>
                        )}
                        <div className="absolute top-2.5 left-2.5 bg-[#091F5C]/90 backdrop-blur-md px-2.5 py-0.5 rounded-full text-[10px] font-bold text-white uppercase tracking-wider">
                          {prop.borough || m.property_borough}
                        </div>
                        <div className="absolute bottom-2.5 right-2.5 bg-white/95 px-2.5 py-1 rounded-lg text-xs font-bold text-[#091F5C] shadow">
                          £{(prop.rent_pcm || m.property_rent_pcm)?.toLocaleString()} PCM
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#E8F2FE] text-[#334DAF] border border-[#D0E4FE]">
                            {m.similarity_tag || "Hand-Picked by Concierge"}
                          </span>
                          <span className="text-xs text-[#4A5B78] font-medium">
                            {prop.bedrooms || m.property_bedrooms} Bed • {prop.property_type || "Apartment"}
                          </span>
                        </div>

                        <h3 className="font-bold text-[#091F5C] text-sm font-heading line-clamp-1">
                          {prop.title || m.property_title}
                        </h3>

                        {/* Concierge Recommendation Note — tenant-facing public_note preferred */}
                        {m.public_note ? (
                          <div className="p-3 rounded-xl bg-white border border-[#D0E4FE] shadow-sm text-xs text-[#091F5C]" data-testid={`match-public-note-${m.id}`}>
                            <div className="text-[9px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold mb-1">
                              Why we picked this
                            </div>
                            <p className="leading-relaxed">{m.public_note}</p>
                          </div>
                        ) : m.concierge_notes ? (
                          <div className="p-3 rounded-xl bg-[#E8F2FE]/70 border border-[#D0E4FE] text-xs text-[#091F5C] italic" data-testid={`match-concierge-note-${m.id}`}>
                            "{m.concierge_notes}"
                          </div>
                        ) : null}

                        {/* Features Tags */}
                        {prop.key_features && prop.key_features.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {prop.key_features.slice(0, 3).map((f, idx) => (
                              <span key={idx} className="text-[10px] bg-[#E8F2FE] text-[#4A5B78] px-2 py-0.5 rounded">
                                {f}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="p-5 pt-0 border-t border-slate-100 space-y-2 mt-4">
                      <div className="flex gap-2 pt-3">
                        <button
                          type="button"
                          onClick={() => setSelectedMatchForViewing(m)}
                          data-testid={`request-viewing-btn-${m.id}`}
                          className="flex-1 py-2 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-xs flex items-center justify-center space-x-1 shadow"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Request Viewing</span>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleShortlistMatch(m.id, m.status)}
                          data-testid={`shortlist-match-btn-${m.id}`}
                          title={isShortlisted ? "Remove from Shortlist" : "Save to Shortlist"}
                          className={`p-2 rounded-xl border transition-colors ${
                            isShortlisted ? "bg-rose-50 border-rose-300 text-rose-600" : "border-[#E5EBF5] text-slate-400 hover:text-[#4A5B78]"
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${isShortlisted ? "fill-rose-500 text-rose-500" : ""}`} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeclineMatch(m.id)}
                          data-testid={`decline-match-btn-${m.id}`}
                          title="Not Suitable"
                          className="p-2 rounded-xl border border-[#E5EBF5] text-slate-400 hover:text-[#4A5B78] hover:bg-[#F9FBFF]"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {m.status === "Viewing Requested" && (
                        <div className="text-[11px] text-[#334DAF] bg-[#E8F2FE] px-2 py-1 rounded text-center font-medium">
                          Viewing Requested • Host Confirmation Pending
                        </div>
                      )}
                      {m.status === "Viewing Confirmed" && (
                        <div className="text-[11px] text-[#334DAF] bg-[#E8F2FE] px-2 py-1 rounded text-center font-bold flex items-center justify-center space-x-1">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Viewing Confirmed</span>
                        </div>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SECTION 3: Viewing Schedule Tracker */}
        <div className="space-y-4 pt-4">
          <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-[#334DAF]" />
            <span>Scheduled Property Viewings ({viewings.length})</span>
          </h2>

          {viewings.length === 0 ? (
            <div className="p-6 rounded-2xl bg-white border border-[#E5EBF5] text-center text-xs text-[#4A5B78]">
              No private viewings requested yet. Click "Request Viewing" on any matched property above.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#E5EBF5] divide-y divide-slate-100 overflow-hidden shadow-sm">
              {viewings.map((v) => (
                <div key={v.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-[#091F5C] text-sm">{v.property_title}</span>
                      <span className="text-xs text-[#4A5B78]">• {v.property_borough}</span>
                    </div>
                    <div className="text-xs text-[#4A5B78] flex items-center space-x-4">
                      <span className="flex items-center space-x-1">
                        <Calendar className="w-3.5 h-3.5 text-[#334DAF]" />
                        <span>{v.preferred_date}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Clock className="w-3.5 h-3.5 text-[#334DAF]" />
                        <span>{v.preferred_time_slot}</span>
                      </span>
                    </div>
                    {v.comments && (
                      <p className="text-[11px] text-[#4A5B78] italic">"{v.comments}"</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      v.status === "Confirmed" 
                        ? "bg-[#D0E4FE] text-emerald-800 border border-[#D0E4FE]"
                        : "bg-[#D0E4FE] text-amber-800 border border-[#D0E4FE]"
                    }`}>
                      {v.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 4: FIND LET Move Dispatch Inquiries */}
        {moveRequests.length > 0 && (
          <div className="space-y-4 pt-4">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
              <Truck className="w-5 h-5 text-[#334DAF]" />
              <span>FIND LET Move Dispatch Requests</span>
            </h2>

            <div className="bg-white rounded-2xl border border-[#E5EBF5] divide-y divide-slate-100 overflow-hidden shadow-sm">
              {moveRequests.map((req) => (
                <div key={req.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-[#091F5C] text-sm">
                        {req.pickup_postcode} &rarr; {req.delivery_postcode} ({req.property_size})
                      </span>
                      <span className="text-xs text-[#334DAF] font-medium">Target Date: {req.move_date}</span>
                    </div>
                    <div className="text-xs text-[#4A5B78]">
                      Services: <span className="font-semibold">{req.services?.join(", ")}</span>
                    </div>
                    {req.assigned_partner && (
                      <div className="text-xs text-[#334DAF] font-bold">
                        Assigned Partner: {req.assigned_partner} {req.quote_amount ? `(Quote: £${req.quote_amount})` : ""}
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D0E4FE] text-purple-800 border border-[#D0E4FE]">
                      {req.dispatch_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* VIEWING REQUEST MODAL */}
      {selectedMatchForViewing && (
        <div className="fixed inset-0 z-50 bg-[#091F5C]/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl border border-[#E5EBF5]">
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <h3 className="font-bold text-[#091F5C] font-heading text-base">
                  Request Private Viewing
                </h3>
                <p className="text-xs text-[#4A5B78]">
                  {selectedMatchForViewing.property_title} ({selectedMatchForViewing.property_borough})
                </p>
              </div>
              <button 
                onClick={() => setSelectedMatchForViewing(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-[#4A5B78]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleRequestViewingSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  required
                  value={viewingForm.preferred_date}
                  onChange={(e) => setViewingForm({ ...viewingForm, preferred_date: e.target.value })}
                  data-testid="viewing-modal-date-input"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1">
                  Preferred Time Slot *
                </label>
                <select
                  value={viewingForm.preferred_time_slot}
                  onChange={(e) => setViewingForm({ ...viewingForm, preferred_time_slot: e.target.value })}
                  data-testid="viewing-modal-timeslot-select"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                >
                  <option value="Morning (09:00 - 12:00)">Morning (09:00 - 12:00)</option>
                  <option value="Afternoon (12:00 - 16:00)">Afternoon (12:00 - 16:00)</option>
                  <option value="Evening (16:00 - 19:00)">Evening (16:00 - 19:00)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1">
                  Notes for Host / Porter
                </label>
                <textarea
                  rows={2}
                  value={viewingForm.comments}
                  onChange={(e) => setViewingForm({ ...viewingForm, comments: e.target.value })}
                  data-testid="viewing-modal-comments-input"
                  className="w-full px-3.5 py-2 rounded-xl border border-[#E5EBF5] text-xs"
                />
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setSelectedMatchForViewing(null)}
                  className="px-4 py-2 rounded-xl border border-[#D0E4FE] text-[#4A5B78] text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={viewingSubmitting}
                  data-testid="submit-viewing-modal-btn"
                  className="px-5 py-2 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white text-xs font-bold shadow"
                >
                  {viewingSubmitting ? "Submitting..." : "Confirm Viewing Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
